/**
 * Modal szczegolow korekty (GET /kontrola-korekt/<zrodlo_id>): naglowek korekty, faktura
 * korygowana (numer, data, sklep, link do faktury w systemie, odznaka zrodla/pewnosci,
 * select kandydatek przy pewnosci 'niska'), tabela pozycji: Produkt z SFD | Produkt w systemie |
 * Ilosc na fakturze | Zwrot | Cena netto | Wartosc | uwaga (braki na czerwono), lista brakujacych,
 * w stopce "Dodaj korektę" (ten sam flow co w tabeli - AkcjeDodaj).
 *
 * Naglowek bierze z odpowiedzi szczegolow, a gdy backend ich nie dolozy - z wiersza tabeli (`wiersz`).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { kontrolaKorektService } from '../../services/kontrolaKorektService';
import { formatujKwote, formatujDate, formatujIlosc, komunikatBleduAkcji } from './format';
import { ZnacznikStatusuKorekty, OdznakaZrodlaFaktury, AkcjeDodaj } from './TabelaKorekt';

const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const btnWarning = { ...btnSecondary, padding: '4px 10px', fontSize: '11px', border: '1px solid var(--warning)', background: 'var(--warning)', color: '#fff' };
const th = { padding: '4px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'top' };
const prawa = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const etykieta = { color: 'var(--text-muted)', fontSize: '11px' };

const SzczegolyKorektyModal = ({ zrodloId, wiersz, onZamknij, onZmiana, onDodano }) => {
  const [dane, setDane] = useState(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);
  const [wybieranie, setWybieranie] = useState(false);
  const [wybor, setWybor] = useState('');
  const [wymusPytanie, setWymusPytanie] = useState(null);   // {faktura_id, komunikat} po 400 (inny sklep)

  const laduj = useCallback(async () => {
    setLadowanie(true);
    try {
      const d = await kontrolaKorektService.szczegoly(zrodloId);
      setDane(d || {});
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać szczegółów korekty');
    } finally {
      setLadowanie(false);
    }
  }, [zrodloId]);

  useEffect(() => { laduj(); }, [laduj]);

  // Naglowek: szczegoly maja pierwszenstwo, wiersz z tabeli jako zapas
  const k = { ...(wiersz || {}), ...(dane || {}) };
  const faktura = dane?.faktura || wiersz?.faktura || null;
  const pozycje = Array.isArray(dane?.pozycje) ? dane.pozycje : [];
  const brakujace = Array.isArray(dane?.brakujace) ? dane.brakujace : [];
  const kandydaci = Array.isArray(dane?.kandydaci) && dane.kandydaci.length > 0 ? dane.kandydaci : (Array.isArray(wiersz?.kandydaci) ? wiersz.kandydaci : []);
  const pokazSelect = kandydaci.length > 0 && (faktura?.pewnosc === 'niska' || kandydaci.length > 1 || !faktura);
  const sumaNetto = pozycje.reduce((a, p) => a + (Number(p.netto) || 0), 0);
  const sumaBrutto = pozycje.reduce((a, p) => a + (Number(p.brutto) || 0), 0);

  const wybierz = async (fakturaId, wymus = false) => {
    if (!fakturaId) return;
    setWybieranie(true);
    try {
      const w = await kontrolaKorektService.wybierzFakture(zrodloId, fakturaId, wymus);
      setWymusPytanie(null);
      setWybor('');
      toast.success(`Wybrano fakturę ${w?.faktura_numer || `#${fakturaId}`} — przeliczono pozycje`);
      await laduj();
      if (onZmiana) onZmiana(w, wiersz);
    } catch (err) {
      if (err?.status === 400 && !wymus) {
        setWymusPytanie({ faktura_id: fakturaId, komunikat: komunikatBleduAkcji(err, 'Sklep faktury różni się od sklepu korekty.') });
      } else if (!err?.isAuthError) {
        toast.error(komunikatBleduAkcji(err, 'Nie udało się wybrać faktury.'));
      }
    } finally {
      setWybieranie(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="modal-korekta-tytul" data-testid="modal-szczegoly-korekty"
        style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '960px', padding: '1.25rem', fontSize: '12px', color: 'var(--text)', maxHeight: '90vh', overflow: 'auto' }}>

        {/* Naglowek korekty */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div>
            <h5 id="modal-korekta-tytul" style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700 }}>
              Korekta {k.numer || `#${zrodloId}`}
            </h5>
            <div data-testid="korekta-naglowek" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <ZnacznikStatusuKorekty status={k.status} />
              <span><span style={etykieta}>data </span>{formatujDate(k.data)}</span>
              <span><span style={etykieta}>sklep </span><strong>{k.sklep?.nazwa || 'nieznany adres'}</strong></span>
              <span><span style={etykieta}>brutto </span><strong style={{ color: 'var(--orange)' }}>{formatujKwote(k.brutto)}</strong></span>
              {k.netto !== undefined && k.netto !== null && <span><span style={etykieta}>netto </span>{formatujKwote(k.netto)}</span>}
              {k.pdf && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', color: 'var(--purple)', backgroundColor: 'var(--purple-light)' }}>{k.pdf_zrodlo === 'portal' ? 'PDF z portalu' : 'PDF z maila'}</span>}
            </div>
            {k.komunikat && <div data-testid="korekta-komunikat" style={{ marginTop: '4px', fontSize: '11px', color: k.status === 'gotowa' ? 'var(--text-muted)' : 'var(--warning)' }}>{k.komunikat}</div>}
            {k.korekta?.numer && (
              <div data-testid="korekta-w-systemie" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--info)' }}>
                W systemie: {k.korekta.id ? <Link to={`/purchase-invoices/${k.korekta.id}`} style={{ color: 'var(--info)', fontWeight: 600 }}>{k.korekta.numer}</Link> : <strong>{k.korekta.numer}</strong>}
                {k.korekta.wz && <> · WZ {k.korekta.wz}</>}
              </div>
            )}
          </div>
          <button type="button" onClick={onZamknij} aria-label="Zamknij" style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>✕</button>
        </div>

        {blad && <div data-testid="modal-blad" style={{ padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', marginBottom: '10px' }}>{blad}</div>}

        {/* Faktura korygowana */}
        <div data-testid="faktura-korygowana-box" style={{ padding: '8px 10px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg)', marginBottom: '10px' }}>
          <div style={{ fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Faktura korygowana</div>
          {faktura?.numer ? (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              {faktura.w_systemie && faktura.id
                ? <Link to={`/purchase-invoices/${faktura.id}`} data-testid="link-faktura" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>{faktura.numer}</Link>
                : <strong style={{ color: faktura.w_systemie === false ? 'var(--warning)' : 'var(--text)' }}>{faktura.numer}</strong>}
              {faktura.w_systemie === false && <span style={{ color: 'var(--warning)' }}>brak w systemie — najpierw dodaj ją w Kontroli faktur</span>}
              {faktura.data && <span><span style={etykieta}>data </span>{formatujDate(faktura.data)}</span>}
              {faktura.sklep?.nazwa && <span><span style={etykieta}>sklep </span>{faktura.sklep.nazwa}</span>}
              {faktura.brutto !== undefined && faktura.brutto !== null && <span><span style={etykieta}>brutto </span>{formatujKwote(faktura.brutto)}</span>}
              {Array.isArray(faktura.pozycje) && faktura.pozycje.length > 0 && <span style={etykieta}>pozycji na fakturze: {faktura.pozycje.length}</span>}
              <OdznakaZrodlaFaktury zrodlo={faktura.zrodlo} pewnosc={faktura.pewnosc} />
            </div>
          ) : (
            <div style={{ color: k.status === 'brak_faktury' ? 'var(--danger)' : 'var(--text-muted)' }}>
              {ladowanie ? 'Ładowanie…' : 'Nie ustalono faktury korygowanej'}
            </div>
          )}
          {pokazSelect && (
            <div style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                Kandydatki{' '}
                <select aria-label="Wybierz fakturę korygowaną" data-testid="modal-select-kandydatki" value={wybor} disabled={wybieranie}
                  onChange={(e) => { setWybor(e.target.value); wybierz(Number(e.target.value)); }}
                  style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', minWidth: '260px' }}>
                  <option value="">— wybierz fakturę ręcznie —</option>
                  {kandydaci.map(c => (
                    <option key={c.faktura_id} value={String(c.faktura_id)}>
                      {c.numer} · {formatujDate(c.data)}{c.pokrycie !== undefined && c.pokrycie !== null ? ` · pokrycie ${Math.round(Number(c.pokrycie) * 100)}%` : ''}{faktura?.id === c.faktura_id ? ' (obecna)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              {wybieranie && <span style={etykieta}>Zapisuję…</span>}
            </div>
          )}
          {wymusPytanie && (
            <div data-testid="wymus-pytanie" style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--warning-light)', color: 'var(--warning)', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{wymusPytanie.komunikat}</span>
              <button type="button" data-testid="btn-wymus" onClick={() => wybierz(wymusPytanie.faktura_id, true)} disabled={wybieranie} style={btnWarning}>Wymuś mimo to</button>
              <button type="button" onClick={() => setWymusPytanie(null)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Anuluj</button>
            </div>
          )}
        </div>

        {/* Pozycje */}
        <div style={{ overflow: 'auto', marginBottom: '10px' }}>
          <table data-testid="tabela-pozycji" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Produkt z SFD</th>
                <th style={th}>Produkt w systemie</th>
                <th style={{ ...th, textAlign: 'right' }}>Ilość na fakturze</th>
                <th style={{ ...th, textAlign: 'right' }}>Zwrot</th>
                <th style={{ ...th, textAlign: 'right' }}>Cena netto</th>
                <th style={{ ...th, textAlign: 'right' }}>Wartość</th>
                <th style={th}>Uwaga</th>
              </tr>
            </thead>
            <tbody>
              {pozycje.map((p, i) => {
                const brak = !p.produkt;
                return (
                  <tr key={p.lp ?? i} data-testid="pozycja" style={{ backgroundColor: brak ? 'var(--danger-light)' : (i % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)') }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{p.nazwa || '—'}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>kod SFD {p.kod_sfd || '—'}{p.vat !== undefined && p.vat !== null ? ` · VAT ${p.vat}%` : ''}</div>
                    </td>
                    <td style={{ ...td, color: brak ? 'var(--danger)' : 'var(--text)' }}>
                      {brak ? <span data-testid="brak-produktu" style={{ fontWeight: 600 }}>brak produktu w systemie</span> : (
                        <>
                          <div style={{ fontWeight: 600 }}>{p.produkt.nazwa || `#${p.produkt.id}`}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>EAN {p.produkt.ean || '—'}{p.produkt.id ? ` · #${p.produkt.id}` : ''}</div>
                        </>
                      )}
                    </td>
                    <td style={{ ...prawa, color: p.pozycja_faktury ? 'var(--text)' : 'var(--text-muted)' }}>
                      {p.pozycja_faktury ? formatujIlosc(p.pozycja_faktury.ilosc) : '—'}
                      {p.pozycja_faktury?.cena_netto !== undefined && p.pozycja_faktury?.cena_netto !== null && Number(p.pozycja_faktury.cena_netto) !== Number(p.cena_netto) && (
                        <div style={{ fontSize: '10px', color: 'var(--warning)' }}>cena na FS {formatujKwote(p.pozycja_faktury.cena_netto)}</div>
                      )}
                    </td>
                    <td style={{ ...prawa, fontWeight: 600, color: 'var(--orange)' }}>{formatujIlosc(p.ilosc)}</td>
                    <td style={prawa}>{formatujKwote(p.cena_netto)}</td>
                    <td style={prawa} title={`netto ${formatujKwote(p.netto)}`}>{formatujKwote(p.brutto)}</td>
                    <td style={{ ...td, color: brak || p.ostrzezenie ? 'var(--danger)' : 'var(--text-muted)' }}>{p.ostrzezenie || (brak ? 'pozycja nie wejdzie do korekty' : '')}</td>
                  </tr>
                );
              })}
              {pozycje.length > 0 && (
                <tr data-testid="pozycje-suma">
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Razem (netto {formatujKwote(sumaNetto)})</td>
                  <td style={{ ...prawa, fontWeight: 700, color: 'var(--orange)' }}>{formatujKwote(sumaBrutto)}</td>
                  <td style={td} />
                </tr>
              )}
            </tbody>
          </table>
          {!ladowanie && pozycje.length === 0 && (
            <div data-testid="brak-pozycji" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              Brak pozycji — pobierz pozycje z SFD (przycisk w nagłówku strony)
            </div>
          )}
          {ladowanie && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie…</div>}
        </div>

        {/* Braki produktow */}
        {brakujace.length > 0 && (
          <div data-testid="brakujace" style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              Brak {brakujace.length} {brakujace.length === 1 ? 'produktu' : 'produktów'} w systemie — korekta nie wejdzie, parser nie zakłada produktów
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '11px' }}>
              {brakujace.map((b, i) => (
                <li key={`${b.kod_sfd || i}`}>{b.nazwa || '—'} <span style={{ color: 'var(--text-muted)' }}>(kod SFD {b.kod_sfd || '—'}, ilość {formatujIlosc(b.ilosc)})</span></li>
              ))}
            </ul>
            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text)' }}>Dodaj te produkty z cennika i kliknij „Przelicz”.</div>
          </div>
        )}

        {/* Stopka */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={onZamknij} style={btnSecondary} data-testid="modal-zamknij">Zamknij</button>
          {k.status !== 'dodana' && k.status !== 'pominieta' && !ladowanie && (
            <AkcjeDodaj wiersz={{ ...k, zrodlo_id: zrodloId }} kompakt={false}
              onDodano={(wynik, w) => { if (onDodano) onDodano(wynik, w); laduj(); }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SzczegolyKorektyModal;
